import { useEffect, useRef, useState } from 'react';
import { feedOf, flashOf, nextBeat, typingNow, type CommentaryPost } from '@edh/shared';
import { request } from './api';
import type { LiveSession } from './live';

/**
 * Ask the server whether the feed owes the table anything.
 *
 * Cheap and idempotent: the server works out the beat itself and claims it with
 * a conditional write, so four phones asking at once produces one batch and
 * three no-ops. That is why this is safe to call from every phone rather than
 * electing one to do it.
 */
export const askCommentary = (id: string) =>
  request<{ wrote: number }>('POST', `/games/${id}/commentary`);

/** How often a phone checks whether a beat has come due. */
const CHECK_MS = 10_000;

/**
 * A phone waits a moment before asking, and each waits a different moment.
 *
 * Without this, four phones polling the same game fire the same request in the
 * same tick and three of them pay a round trip to be told no. Jitter is not
 * correctness here — the server is already safe — it is just manners.
 */
const jitter = () => Math.floor(Math.random() * 4_000);

export interface CommentaryFeed {
  /** Posts that have landed, oldest first. */
  posts: CommentaryPost[];
  /** Handles composing right now, from the model or from the schedule. */
  typing: string[];
  /** The post to hold over the table for a moment, or null. */
  flash: CommentaryPost | null;
}

/**
 * The feed, revealed on a clock.
 *
 * Every post carries the moment it is due, decided by the server, so this hook
 * does nothing cleverer than re-render once a second and show what has come
 * due. That is what makes four phones show the same joke at the same instant
 * without any of them being told to.
 */
export function useCommentary(session: LiveSession): CommentaryFeed {
  const [now, setNow] = useState(() => Date.now());

  /*
   * The session, readable from inside a timer without going stale.
   *
   * The first version closed over `session` directly and listed only the game
   * id in the effect's dependencies, which meant the interval kept reading
   * whatever the session had been on the render that started it — a game that
   * was still null while the first poll was in flight. It never saw a started
   * game, so it never asked for a beat, and nothing failed: the feed was simply
   * empty forever. A ref updated every render is what the live-game hook next
   * door already does for the same reason.
   */
  const live = useRef(session);
  live.current = session;
  const asking = useRef(false);

  // A second is finer than anything the schedule needs and coarse enough that
  // the feed is not a render loop.
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 1_000);
    return () => window.clearInterval(timer);
  }, []);

  const gameId = session.game?.id ?? null;
  useEffect(() => {
    if (!gameId) return;
    let cancelled = false;

    const tick = async () => {
      const current = live.current.game;
      const seats = live.current.view?.seats;
      if (!current || !seats || asking.current) return;
      if (!nextBeat(current, seats, current.commentary)) return;

      asking.current = true;
      try {
        await new Promise((r) => setTimeout(r, jitter()));
        if (cancelled) return;
        const { wrote } = await askCommentary(current.id);
        // Only worth a refetch if it actually wrote — the common answer is that
        // somebody else's phone got there first, and the next poll brings it.
        if (wrote > 0 && !cancelled) await live.current.refresh();
      } catch {
        // A feed that fails to appear is not an error anybody needs told about.
      } finally {
        asking.current = false;
      }
    };

    void tick();
    const timer = window.setInterval(() => void tick(), CHECK_MS);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [gameId]);

  const commentary = session.game?.commentary;
  return {
    posts: feedOf(commentary, now),
    typing: typingNow(commentary, now),
    flash: flashOf(commentary, now),
  };
}
